import { PregnancyArticle, VideoPlayer, VideoTimeFrame } from "@aph/ui-lib-humanx";
import { format } from "date-fns/format";
import React, { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import ShareIcon from "$/icons/shareIcon";
import ArticleContent from "@/components/ArticlePage/Components/ArticleContent";
import FAQComponent from "@/components/FAQ";
import ShareIconModal from "@/components/ShareModal";
import { VideoAttributesInterface, VideoDetailInterface } from "@/components/VideoPage/api/interface";
import { COMPONENT_TYPES } from "@/components/VideoPage/constants";
import { COMPONENT_TYPES as COMMON_COMPONENT_TYPES, TYPE_VIDEO } from "@/helpers/constants";
import DeviceSelector from "@/components/VideoPage/redux/selectors/userAgent";
import VideoSelector from "@/components/VideoPage/redux/selectors/videoData";
import { DeviceInfo } from "@/interface/deviceInfo";
import { useAppSelector } from "@/redux/hooks";
import IconPlus from "$/icons/icon-plus.svg";
import DoctorsWidget from "@/components/DoctorsWidget";
import Bookmark from "@/components/BookmarkAction";
import DiagnosticsWidget from "@/components/DiagnosticsWidget";
import BasicAccordionUnit from "@/components/VideoPage/components/BasicAccordionUnit";
import styles from "./video.module.scss";

interface ComponentMappingProps {
  data: any;
  extras: any;
}

const VideoComponentMapping = (props: ComponentMappingProps): React.ReactElement => {
  const { data, extras } = props;
  const { currentSpeciality, doctorsArray, diagnosticsArray } = extras;

  const [openShareModal, setOpenShareModal] = useState<boolean>(false);
  const type = data?.ComponentType;
  const videoInfoObject: VideoDetailInterface = useAppSelector(VideoSelector);
  const videoDataObject: VideoAttributesInterface = videoInfoObject?.data?.[0]?.attributes;
  const deviceType: DeviceInfo = useAppSelector(DeviceSelector);
  const router = useRouter();

  const videoRef = useRef<HTMLVideoElement>(null);
  const videoRefId = "videopage-elem";

  const handleVideoTime = (seconds: number) => {
    const videoElement = document.getElementById(videoRefId) as HTMLVideoElement;

    if (videoElement) {
      videoElement.currentTime = seconds;
    } else {
      console.error("Video element not found");
    }
  };

  const handleAuthorClick = () => {
    videoDataObject?.AuthorUrl && router.push(videoDataObject?.AuthorUrl);
  };

  switch (type) {
    case COMPONENT_TYPES.BannerInfo:
      return (
        <>
          <PregnancyArticle
            articleLabel={videoDataObject?.Category?.data?.attributes?.CategoryName}
            title={videoDataObject?.Title}
            subTitle={videoDataObject?.Description}
            articleDate={format(new Date(videoDataObject?.PostedOn), "do MMM yyyy")}
            publisherName={videoDataObject?.AuthorName}
            authorUrl={videoDataObject?.AuthorUrl}
            onAuthorClick={handleAuthorClick}
            articleReadTime={`${videoDataObject?.VideoTime} read`}
            isSocialMedia={true}
            actionsData={[
              {
                component: (
                  <Bookmark
                    isFromListingPage={true}
                    type={TYPE_VIDEO}
                    itemId={Number(videoInfoObject?.data?.[0]?.id)}
                  />
                ),
              },
              {
                component: deviceType?.isMobile ? <span className="icon-ShareFat" /> : <ShareIcon />,
                onClick: () => setOpenShareModal(true),
              },
            ]}
            videoSection={
              <VideoPlayer
                poster={videoDataObject?.Thumbnail?.data?.attributes?.url}
                fallbackImage={videoDataObject?.Thumbnail?.data?.attributes?.url}
                src={videoDataObject?.Video?.data?.attributes?.url}
                controls={true}
                autoplay={false}
                loop={false}
                muted={false}
                width="892"
                height="360"
                videoRef={videoRef}
                id={videoRefId}
              />
            }
            authorIcon={<IconPlus width={13} height={13} />}
          />
          <ShareIconModal {...{ openPopup: openShareModal, setOpenPopup: setOpenShareModal }} />
        </>
      );
    case COMPONENT_TYPES.Timeframes:
      const timeFrameIntervals = data?.TimeframeList?.map((item: any) => {
        const [minutes, seconds] = item?.Time?.split(":")?.map(Number);
        const totalSeconds = minutes * 60 + seconds;

        return {
          seconds: totalSeconds,
          imageUrl: `${videoDataObject?.Video?.data?.attributes?.url}/ik-thumbnail.jpg`,
          frameTime: item?.Time,
          frameTitle: item?.Summary,
          onTimeFrameClick: () => handleVideoTime(totalSeconds),
        };
      });

      return (
        <VideoTimeFrame
          isVerticalScroll={deviceType?.isDesktop}
          title="Key Timeframes"
          wrapperHeight={"auto"}
          videoFrame={timeFrameIntervals}
          className={styles.timeFrameTopMargin}
        />
      );

    case COMPONENT_TYPES.ContentDescription:
      return <ArticleContent id={data?.id} subTitle={data?.Description} />;
    case COMMON_COMPONENT_TYPES.DoctorConsult:
      return (
        <DoctorsWidget
          data={data}
          userAgent={deviceType}
          currentSpeciality={currentSpeciality}
          doctors={doctorsArray}
          utm_medium="video-page"
        />
      );
    case COMMON_COMPONENT_TYPES.LabTest:
      return (
        <div className="spacingBetweenSections">
          <DiagnosticsWidget
            data={data}
            userAgent={deviceType}
            diagnostics={diagnosticsArray}
            utm_medium="video-page"
          />
        </div>
      );
    case COMPONENT_TYPES.FAQ:
      return <FAQComponent data={data} />;
    case COMPONENT_TYPES.VideoTranscript:
      return data?.Transcript ? (
        <div className={styles.transcriptWrapper}>
          <BasicAccordionUnit
            panels={[
              {
                title: "Transcript",
                description: data?.Transcript,
              },
            ]}
          />
        </div>
      ) : null;
    default:
      return (
        <>
          <br />
        </>
      );
  }
};

export default VideoComponentMapping;
